import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Box,
    Text,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/pagination'
import {Pagination, Navigation} from 'swiper/modules'

import Card from './partials/card'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const CategoryRowTall = ({
    minDisplayMdTo4,
    title,
    titleTag = 'h2',
    titleColor = 'black',
    titleAlignment = 'center',
    titleFontFamily = 'caslon',
    headlineBackground = 'white',
    typeId,
    name,
    regions
}) => {
    const pdTagManager = usePdTagManager()
    const minDisplayMD = minDisplayMdTo4 ? 4 : 3
    const slidesPerView = useBreakpointValue({base: 'auto', md: minDisplayMD, lg: 4})
    const spaceBetween = useBreakpointValue({base: 15, md: 10})
    useEffect(() => {
        pdTagManager.viewComponent(typeId, name, regions?.[0]?.components?.length)
    }, [])
    const aspectRatio = useBreakpointValue({base: '323:364', md: '462:630'})
    return (
        <Stack gap="0" paddingTop="10px" paddingBottom={{md: '20px'}} paddingX={{md: '20px'}}>
            {title && (
                <Box width="100%" background={headlineBackground}>
                    <Box
                        maxWidth={titleAlignment === 'center' ? '700px' : '100%'}
                        textAlign={titleAlignment}
                        paddingTop={{base: 0, xs: '10px', lg: '20px', xl: '30px'}}
                        paddingBottom={{base: '20px', lg: '30px', xl: '40px'}}
                        paddingX={{base: '15px', md: 0}}
                        marginX="auto"
                    >
                        <Text
                            as={titleTag}
                            color={titleColor}
                            fontFamily={titleFontFamily}
                            fontWeight="500"
                            fontSize={{base: '36px', lg: '42px'}}
                            lineHeight="normal"
                            letterSpacing={{base: '0.17px', lg: '0. 21px'}}
                            margin="0 20px"
                        >
                            {title}
                        </Text>
                    </Box>
                </Box>
            )}

            <Box
                maxWidth="100vw"
                sx={{
                    '.swiper-wrapper': {
                        paddingLeft: {base: '15px', md: 0}
                    },
                    '.component-video': {
                        aspectRatio: `${aspectRatio?.replace(':', ' / ')} !important`,
                        width: '100%',
                        height: 'auto',
                        htmlWidth: aspectRatio?.split(':')[0],
                        htmlHeight: aspectRatio?.split(':')[1]
                    },
                    '.swiper': {
                        '--swiper-pagination-bottom': '-7px',
                        paddingRight: {base: '30px', md: 0},
                        paddingBottom: {base: '30px', md: 0},

                        '&:not(.swiper-initialized)': {
                            paddingRight: 0,
                            overflowX: 'hidden',

                            '.swiper-wrapper': {
                                display: 'flex',
                                overflowX: 'auto',
                                gap: '15px'
                            },
                            '.swiper-slide': {
                                flex: {
                                    base: '0 0 323px',
                                    xs: '0 0 373px',
                                    md: `0 0 calc(((100vw - 20px - var(--scrollbar-width)) / ${minDisplayMD}) - 10px)`,
                                    lg: '0 0 calc(((100vw - 20px - var(--scrollbar-width)) / 4) - 10px)'
                                }
                            }
                        },

                        '&:hover': {
                            '.swiper-button-prev, .swiper-button-next': {
                                opacity: 1
                            }
                        }
                    },
                    '.swiper-slide': {
                        width: {
                            base: '323px',
                            xs: '373px',
                            md: `calc(((100vw - 40px - var(--scrollbar-width)) / ${minDisplayMD}))`,
                            lg: 'calc(((100vw - 40px - var(--scrollbar-width)) / 4))'
                        }
                    },
                    '.swiper-pagination': {
                        display: {base: 'block', md: 'none'},
                        position: 'relative'
                    },
                    '.swiper-button-prev, .swiper-button-next': {
                        '--swiper-navigation-size': '45px',
                        display: {base: 'none', md: 'flex'},
                        top: '50%',
                        borderRadius: '5px',
                        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.20)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',

                        '&.swiper-button-disabled': {
                            display: 'none'
                        }
                    },
                    '.swiper-button-prev': {
                        left: '20px',
                        right: 'initial',
                        transform: 'translateY(-50%) rotate(180deg)'
                    },
                    '.swiper-button-next': {
                        right: '20px',
                        left: 'initial',
                        transform: 'translateY(-50%)'
                    },
                    '.swiper-pagination-bullet': {
                        margin: '1px 10px !important'
                    }
                }}
            >
                <Swiper
                    navigation={true}
                    pagination={{
                        clickable: true
                    }}
                    modules={[Pagination, Navigation]}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    centerInsufficientSlides={true}
                >
                    {regions?.[0]?.components?.map((card, index) => (
                        <SwiperSlide key={index}>
                            <Card
                                {...card?.data}
                                aspectRatio={aspectRatio}
                                typeId={typeId}
                                index={index}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>
        </Stack>
    )
}

CategoryRowTall.propTypes = {
    minDisplayMdTo4: PropTypes.bool,
    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleColor: PropTypes.string,
    titleAlignment: PropTypes.string,
    titleFontFamily: PropTypes.string,
    headlineBackground: PropTypes.string,

    image1: PropTypes.string,
    imageAlt1: PropTypes.string,
    imageUrl1: PropTypes.string,
    imageTitle1: PropTypes.string,
    imageTitleTag1: PropTypes.string,
    imageTitleFontFamily1: PropTypes.string,

    image2: PropTypes.string,
    imageAlt2: PropTypes.string,
    imageUrl2: PropTypes.string,
    imageTitle2: PropTypes.string,
    imageTitleTag2: PropTypes.string,
    imageTitleFontFamily2: PropTypes.string,

    image3: PropTypes.string,
    imageAlt3: PropTypes.string,
    imageUrl3: PropTypes.string,
    imageTitle3: PropTypes.string,
    imageTitleTag3: PropTypes.string,
    imageTitleFontFamily3: PropTypes.string,

    image4: PropTypes.string,
    imageAlt4: PropTypes.string,
    imageUrl4: PropTypes.string,
    imageTitle4: PropTypes.string,
    imageTitleTag4: PropTypes.string,
    imageTitleFontFamily4: PropTypes.string,

    typeId: PropTypes.string,
    regions: PropTypes.any,
    name: PropTypes.any
}

export default CategoryRowTall
